/**
 * Shape e validação do formulário de CRUD de planos em
 * `/superadmin/billing/plans`. As RPCs no banco (migration 0055) já
 * têm checks de tipo e constraints — esse schema é a primeira linha
 * de defesa na UI pra feedback rápido sem round-trip.
 *
 * Regex do `key` replica o CHECK de `public.plans.key ~ '^[a-z][a-z0-9_]*$'` —
 * se o usuário escapar do regex no front, o banco ainda bloqueia, mas
 * com mensagem críptica. Melhor pegar no form. */
import { z } from 'zod';

export const PLAN_KEY_REGEX = /^[a-z][a-z0-9_]*$/;
export const PLAN_TIERS = ['free', 'pro', 'enterprise', 'custom'] as const;
export const PLAN_BILLING_CYCLES = ['monthly', 'yearly'] as const;
export const PLAN_ASAAS_BILLING_TYPES = ['UNDEFINED', 'BOLETO', 'CREDIT_CARD', 'PIX'] as const;

export type PlanTier = (typeof PLAN_TIERS)[number];
export type PlanBillingCycle = (typeof PLAN_BILLING_CYCLES)[number];
export type PlanAsaasBillingType = (typeof PLAN_ASAAS_BILLING_TYPES)[number];

/** Labels human-readable pros 2 selects novos da section "Cobrança". */
export const PLAN_BILLING_CYCLE_LABELS: Record<PlanBillingCycle, string> = {
  monthly: 'Mensal',
  yearly: 'Anual',
};
export const PLAN_ASAAS_BILLING_TYPE_LABELS: Record<PlanAsaasBillingType, string> = {
  UNDEFINED: 'Cliente escolhe no checkout (recomendado)',
  BOLETO: 'Só boleto',
  CREDIT_CARD: 'Só cartão de crédito',
  PIX: 'Só PIX',
};

/** Schema raw do form — strings pra inputs, convertidos pra números
 * na mutation antes de enviar ao backend. Campos nullable no banco
 * aceitam string vazia aqui (convertida pra null na RPC via `nullif`). */
export function buildPlanFormSchema(options: { isEditing: boolean }) {
  const keySchema = z
    .string()
    .trim()
    .min(1, 'Chave técnica é obrigatória.')
    .max(64, 'Chave técnica é longa demais.')
    .regex(PLAN_KEY_REGEX, 'Use só minúsculas, dígitos e underscore. Deve começar com letra.');

  return z.object({
    // `key` só editável em modo criação (vira imutável depois — é referenciada
    // em proposals/subscriptions e não vale o risco de quebrar linkagem).
    key: options.isEditing ? keySchema : keySchema,
    name_pt_br: z.string().trim().min(1, 'Nome é obrigatório.').max(120, 'Nome é longo demais.'),
    description: z.string().trim().max(500, 'Descrição é longa demais.').optional().default(''),
    tier: z.enum(PLAN_TIERS),
    // Preços: string permitida vazia (= "preço sob consulta" / Enterprise
    // sem número fixo). Quando preenchido, valida número ≥ 0.
    monthly_price_brl: z
      .string()
      .refine((v) => v === '' || (!isNaN(Number(v)) && Number(v) >= 0), {
        message: 'Preço mensal deve ser um número ≥ 0 (ou vazio = sob consulta).',
      })
      .default(''),
    yearly_price_brl: z
      .string()
      .refine((v) => v === '' || (!isNaN(Number(v)) && Number(v) >= 0), {
        message: 'Preço anual deve ser um número ≥ 0 (ou vazio = sob consulta).',
      })
      .default(''),
    trial_days: z
      .string()
      .refine(
        (v) => v === '' || (!isNaN(Number(v)) && Number(v) >= 0 && Number.isInteger(Number(v))),
        {
          message: 'Trial deve ser um número inteiro ≥ 0.',
        },
      )
      .default('0'),
    // Limites: vazio = ilimitado (null no banco).
    max_users: z
      .string()
      .refine(
        (v) => v === '' || (!isNaN(Number(v)) && Number(v) > 0 && Number.isInteger(Number(v))),
        {
          message: 'Max. usuários deve ser inteiro > 0 (ou vazio = ilimitado).',
        },
      )
      .default(''),
    max_patients: z
      .string()
      .refine(
        (v) => v === '' || (!isNaN(Number(v)) && Number(v) > 0 && Number.isInteger(Number(v))),
        {
          message: 'Max. pacientes deve ser inteiro > 0 (ou vazio = ilimitado).',
        },
      )
      .default(''),
    max_storage_gb: z
      .string()
      .refine(
        (v) => v === '' || (!isNaN(Number(v)) && Number(v) > 0 && Number.isInteger(Number(v))),
        {
          message: 'Storage deve ser inteiro > 0 GB (ou vazio = ilimitado).',
        },
      )
      .default(''),
    max_ai_tokens_monthly: z
      .string()
      .refine(
        (v) => v === '' || (!isNaN(Number(v)) && Number(v) >= 0 && Number.isInteger(Number(v))),
        { message: 'Tokens IA deve ser inteiro ≥ 0 (ou vazio = ilimitado).' },
      )
      .default(''),
    features: z.array(z.string().trim().min(1)).default([]),
    is_public: z.boolean().default(true),
    is_active: z.boolean().default(true),
    sort_order: z
      .string()
      .refine((v) => v === '' || (!isNaN(Number(v)) && Number.isInteger(Number(v))), {
        message: 'Ordem deve ser um número inteiro.',
      })
      .default('100'),
    // Campos de cobrança Asaas (migration 0056):
    billing_cycle: z.enum(PLAN_BILLING_CYCLES),
    asaas_default_billing_type: z.enum(PLAN_ASAAS_BILLING_TYPES),
  });
}

export type PlanFormInput = z.infer<ReturnType<typeof buildPlanFormSchema>>;

/** Converte input cru do form pro payload que a RPC `admin_upsert_plan`
 * espera. Strings vazias viram null (pra nullable columns); preços viram
 * number; booleans mantêm; features JSON stays array. */
export function formToRpcPayload(input: PlanFormInput): Record<string, unknown> {
  const emptyToNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  return {
    key: input.key.trim(),
    name_pt_br: input.name_pt_br.trim(),
    description: input.description?.trim() || null,
    tier: input.tier,
    monthly_price_brl: emptyToNull(input.monthly_price_brl),
    yearly_price_brl: emptyToNull(input.yearly_price_brl),
    trial_days: input.trial_days === '' ? 0 : Number(input.trial_days),
    max_users: emptyToNull(input.max_users),
    max_patients: emptyToNull(input.max_patients),
    max_storage_gb: emptyToNull(input.max_storage_gb),
    max_ai_tokens_monthly: emptyToNull(input.max_ai_tokens_monthly),
    features: input.features,
    is_public: input.is_public,
    is_active: input.is_active,
    sort_order: input.sort_order === '' ? 100 : Number(input.sort_order),
    billing_cycle: input.billing_cycle,
    asaas_default_billing_type: input.asaas_default_billing_type,
  };
}
